import React, { memo, useEffect, useRef } from 'react';
import { Animated, FlatList, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useGetHighlightsQuery } from 'services/cms';
import { CmsHomeHero } from 'types';
import { calcSizeForTv } from 'utils/index';

import CarouselIndicators from 'components/Carousel/CarouselIndicators';
import CarouselItem from 'components/Carousel/CarouselItem';
import FocusView from 'components/FocusView';

const Carousel: React.FC<CmsHomeHero> = (props) => {
  const flatListRef = useRef<FlatList | null>(null);
  const scrollX = useRef(new Animated.Value(0)).current;

  const { data, isLoading } = useGetHighlightsQuery(props.searchPrograms, {
    pollingInterval: 1800000,
    refetchOnMountOrArgChange: true,
  });

  const { width: screenWidth, height: rawScreenHeight } = useWindowDimensions();
  const screenHeight = rawScreenHeight > 748 ? 748 : rawScreenHeight;
  const halfScreenHeight = screenHeight / 2;

  const scrollEventConfig = [{ nativeEvent: { contentOffset: { x: scrollX } } }];
  const onScroll = Animated.event(scrollEventConfig, { useNativeDriver: false });

  const MemoizedCarouselItem = memo(({ item, loading, link }: any) => (
    <View
      style={[
        styles.carouselItem,
        { width: screenWidth - calcSizeForTv(108), height: calcSizeForTv(screenHeight) + 100 },
      ]}>
      <CarouselItem item={item} loading={loading} link={link} />
    </View>
  ));

  useEffect(() => {
    if (data?.items.length) {
      flatListRef.current?.scrollToIndex({ index: 0, animated: true });
      scrollX.setValue(0);
    }
  }, [data, scrollX]);

  const scrollToItem = (n: number) => {
    flatListRef.current?.scrollToIndex({ animated: true, index: n });
  };

  if (!data?.items.length) {
    return null;
  }

  return (
    <FocusView>
      <View
        style={[
          styles.carouselContainer,
          {
            height: calcSizeForTv(screenHeight) + 100,
            marginBottom: -(calcSizeForTv(halfScreenHeight) - 50),
          },
        ]}>
        <FlatList
          horizontal
          bounces
          pagingEnabled
          ref={flatListRef}
          data={data?.items}
          onScroll={onScroll}
          renderItem={({ item }) => (
            <MemoizedCarouselItem item={item} loading={isLoading} link={item.more?.link} />
          )}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ marginBottom: 0 }}
          keyExtractor={(item, index) => `${item.title}_${index}`}
          viewabilityConfig={{ itemVisiblePercentThreshold: 50 }}
        />

        <View
          style={[
            styles.indicatorBox,
            {
              position: 'absolute',
              bottom: calcSizeForTv(halfScreenHeight) + 70,
              left: calcSizeForTv(24),
            },
          ]}>
          <CarouselIndicators
            data={data?.items ?? []}
            scrollX={scrollX}
            scrollToItem={scrollToItem}
          />
        </View>
      </View>
    </FocusView>
  );
};

const styles = StyleSheet.create({
  carouselContainer: {
    width: '100%',
  },
  indicatorBox: {
    height: 0,
    width: '100%',
  },
  carouselItem: {
    flex: 1,
  },
});

export default Carousel;
